/**
 * Refine LiveProvider — Supabase Realtime.
 *
 * Подписки на изменения таблиц orders / products → invalidate React Query
 * → автоматический refetch листингов.
 *
 * Маппинг ресурс → Supabase table:
 *   - "orders"     → public.orders          (новый заказ, смена статуса)
 *   - "products"   → public.products        (изменение остатков, цены)
 *   - "categories" → public.categories      (редко, но полезно для дерева)
 *
 * Channel: "{resource}-changes". Realtime рассылает события на всех
 * подключённых админов одновременно (несколько менеджеров видят апдейты).
 */

import type { LiveProvider } from "@refinedev/core";

import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const REALTIME_RESOURCES: Record<string, string> = {
  orders: "orders",
  products: "products",
  categories: "categories",
  banners: "banners",
};

export const liveProvider: LiveProvider = {
  subscribe: ({ channel, types, params, callback }) => {
    if (!isSupabaseConfigured || !supabase) {
      return null;
    }

    // channel приходит как "resources/{resource}" — извлекаем имя ресурса
    const resource = params?.resource ?? channel.split("/")[1];
    const tableName = REALTIME_RESOURCES[resource as string];
    if (!tableName) {
      return null;
    }

    const eventMap: Record<string, "INSERT" | "UPDATE" | "DELETE"> = {
      created: "INSERT",
      updated: "UPDATE",
      deleted: "DELETE",
    };

    const supaEvents = (types ?? ["created", "updated", "deleted"])
      .map((t) => eventMap[t])
      .filter(Boolean);

    const supaChannel = supabase.channel(`admin-${tableName}`);

    for (const event of supaEvents) {
      supaChannel.on(
        // @ts-expect-error supabase-js types are looser than runtime
        "postgres_changes",
        { event, schema: "public", table: tableName },
        (payload: any) => {
          const reverseMap: Record<string, "created" | "updated" | "deleted"> = {
            INSERT: "created",
            UPDATE: "updated",
            DELETE: "deleted",
          };
          callback({
            channel,
            type: reverseMap[payload.eventType] ?? "updated",
            payload: payload.new ?? payload.old,
            date: new Date(),
          });
        },
      );
    }

    supaChannel.subscribe();

    return supaChannel;
  },

  unsubscribe: (subscription: any) => {
    if (subscription && typeof subscription.unsubscribe === "function") {
      subscription.unsubscribe();
    }
    if (subscription && supabase) {
      supabase.removeChannel(subscription);
    }
  },

  publish: () => {
    /* noop — публикация идёт из backend через triggers */
  },
};
